import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Play, Award } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { formatDuration } from "../lib/format";
import { resolveImageUrl } from "../lib/images";
import { toast } from "sonner";

const MyCourses = () => {
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    fetchMyCourses();
  }, []); // eslint-disable-line

  const handleDownloadCertificate = async (course) => {
    if (!user?.id_number) {
      toast.error("Primero registra tu cédula/pasaporte en Mis Datos");
      navigate("/mis-datos");
      return;
    }
    setDownloadingId(course.id);
    try {
      const response = await axios.get(`${API}/certificate/${course.id}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificado_${course.title.replace(/[^a-z0-9]/gi, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Certificado descargado");
    } catch (e) {
      let msg = "No se pudo descargar el certificado";
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || msg;
        } catch (parseErr) { /* ignore */ }
      } else if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setDownloadingId(null);
    }
  };

  const fetchMyCourses = async () => {
    try {
      const response = await axios.get(`${API}/my-courses`);
      setCourses(response.data);
    } catch (error) {
      console.error("Error fetching courses:", error);
    } finally {
      setLoading(false);
    }
  };

  const inProgressCourses = courses.filter(
    (c) => c.progress_percentage > 0 && c.progress_percentage < 100
  );
  const completedCourses = courses.filter((c) => c.progress_percentage === 100);
  const notStartedCourses = courses.filter((c) => c.progress_percentage === 0);

  const CourseItem = ({ course }) => (
    <div
      className="bg-card/50 backdrop-blur-md border border-white/10 rounded-lg overflow-hidden hover:border-white/20 transition-all"
      data-testid={`my-course-${course.id}`}
    >
      <div className="flex flex-col md:flex-row">
        <div className="md:w-64 aspect-video md:aspect-auto">
          <img src={resolveImageUrl(course.image_url)} alt={course.title} width={1280} height={720} loading="lazy" decoding="async" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h3 className="text-xl font-bold text-white mb-2">{course.title}</h3>
              <p className="text-white/60 text-sm mb-3 line-clamp-2">{course.description}</p>
              <div className="flex items-center gap-4 text-sm text-white/60">
                <span>{course.category}</span>
                <span>•</span>
                <span>{course.level}</span>
                <span>•</span>
                <span>{formatDuration(course.duration_hours)}</span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-white/60">
                  {course.completed_lessons_count} de {course.total_lessons} lecciones
                </span>
                <span className="text-sm text-white font-semibold">
                  {Math.round(course.progress_percentage)}%
                </span>
              </div>
              <Progress value={course.progress_percentage} className="h-2" />
            </div>

            <div className="flex gap-3">
              <Link to={`/player/${course.id}`} className="flex-1">
                <Button
                  className="w-full bg-[#E50914] hover:bg-[#E50914]/90 text-white"
                  data-testid={`continue-btn-${course.id}`}
                >
                  <Play className="w-4 h-4 mr-2" />
                  {course.progress_percentage === 0 ? "Comenzar" : "Continuar"}
                </Button>
              </Link>
              {course.progress_percentage === 100 && (
                <Button
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                  onClick={() => handleDownloadCertificate(course)}
                  disabled={downloadingId === course.id}
                  data-testid={`certificate-btn-${course.id}`}
                >
                  <Award className="w-4 h-4 mr-2" />
                  {downloadingId === course.id ? "Descargando..." : "Certificado"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black">
      <Header />

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-24">
        <h1 className="text-4xl md:text-5xl font-black text-white mb-8">Mis Cursos</h1>

        {loading ? (
          <div className="text-center text-white py-20">Cargando...</div>
        ) : courses.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-white/60 text-xl mb-6">Aún no estás inscrito en ningún curso</p>
            <Link to="/cursos">
              <Button className="bg-[#E50914] hover:bg-[#E50914]/90 text-white">
                Explorar Cursos
              </Button>
            </Link>
          </div>
        ) : (
          <Tabs defaultValue="in-progress" className="w-full">
            <TabsList className="mb-6">
              <TabsTrigger value="in-progress" data-testid="tab-in-progress">
                En Progreso ({inProgressCourses.length})
              </TabsTrigger>
              <TabsTrigger value="completed" data-testid="tab-completed">
                Finalizados ({completedCourses.length})
              </TabsTrigger>
              <TabsTrigger value="not-started" data-testid="tab-not-started">
                Sin comenzar ({notStartedCourses.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="in-progress" className="space-y-4">
              {inProgressCourses.length === 0 ? (
                <p className="text-center text-white/60 py-10">No tienes cursos en progreso</p>
              ) : (
                inProgressCourses.map((course) => <CourseItem key={course.id} course={course} />)
              )}
            </TabsContent>

            <TabsContent value="completed" className="space-y-4">
              {completedCourses.length === 0 ? (
                <p className="text-center text-white/60 py-10">No has completado ningún curso aún</p>
              ) : (
                completedCourses.map((course) => <CourseItem key={course.id} course={course} />)
              )}
            </TabsContent>

            <TabsContent value="not-started" className="space-y-4">
              {notStartedCourses.length === 0 ? (
                <p className="text-center text-white/60 py-10">Todos tus cursos han sido iniciados</p>
              ) : (
                notStartedCourses.map((course) => <CourseItem key={course.id} course={course} />)
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default MyCourses;
